import { useCallback, useEffect, useRef, useState } from "react";
import { TableColumn } from "react-data-table-component";
import {
    Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText,
    DialogTitle, FormControl, IconButton, InputLabel, MenuItem, Paper, Select, Snackbar, TextField, Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import DataGrid from "../../Components/DataGrid/DataGrid";
import { loadFromLocalStorage } from "../../Common/Validation/LocalStorage";
import SupportTicketServices, {
    ISupportTicket, ISupportTicketDetail, TicketStatus
} from "../../Services/SupportTicketServices";

const STATUS_FILTERS: TicketStatus[] = ['new', 'open', 'pending', 'solved', 'closed'];
// "new" is assigned by Zendesk and "closed" is read-only there, so admins can only set these.
const SETTABLE_STATUSES: TicketStatus[] = ['open', 'pending', 'solved'];

const statusColor = (status: TicketStatus): 'default' | 'info' | 'warning' | 'success' => {
    switch (status) {
        case 'new':
        case 'open': return 'info';
        case 'pending':
        case 'hold': return 'warning';
        case 'solved': return 'success';
        default: return 'default';
    }
};

const formatDate = (value?: string) => (value ? new Date(value).toLocaleString() : '');

/** Reads the API's { message } from a failed response, falling back to a generic text. */
const errorMessage = async (response: Response): Promise<string> => {
    try {
        const body = await response.json();
        return body?.message || 'Something went wrong. Please try again.';
    } catch {
        return 'Something went wrong. Please try again.';
    }
};

const SupportTickets = () => {
    const [token, setToken] = useState('');
    const [tickets, setTickets] = useState<ISupportTicket[]>([]);
    const [totalRows, setTotalRows] = useState(0);
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [listLoading, setListLoading] = useState(false);
    const [listError, setListError] = useState('');

    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [detail, setDetail] = useState<ISupportTicketDetail | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState('');

    const [reply, setReply] = useState('');
    const [sending, setSending] = useState(false);
    const [replyError, setReplyError] = useState('');
    const [statusUpdating, setStatusUpdating] = useState(false);
    const [toast, setToast] = useState('');

    // Blocks a second click in the same tick, before React has re-rendered the disabled button.
    const sendLock = useRef(false);

    // Confirmations use an MUI dialog rather than SweetAlert: a SweetAlert popup renders beneath an
    // open MUI dialog and can't take focus, so it would be invisible over the ticket dialog.
    const [confirmState, setConfirmState] = useState<{ title: string; text: string; confirmLabel: string } | null>(null);
    const confirmResolver = useRef<((confirmed: boolean) => void) | null>(null);

    const confirm = (options: { title: string; text: string; confirmLabel: string }) =>
        new Promise<boolean>((resolve) => {
            confirmResolver.current = resolve;
            setConfirmState(options);
        });

    const closeConfirm = (confirmed: boolean) => {
        confirmResolver.current?.(confirmed);
        confirmResolver.current = null;
        setConfirmState(null);
    };

    useEffect(() => {
        const accessToken = loadFromLocalStorage<string>('accessToken');
        if (accessToken) {
            setToken(accessToken.toString());
        } else {
            localStorage.clear();
            window.location.href = '/';
        }
    }, []);

    const loadList = useCallback(async () => {
        if (!token) return;
        setListLoading(true);
        setListError('');
        try {
            const response = await SupportTicketServices.getTickets(token, { page, perPage: rowsPerPage, status: statusFilter, search });
            if (!response.ok) {
                setListError(await errorMessage(response));
                return;
            }
            const result = await response.json();
            setTickets(result.tickets);
            setTotalRows(result.total ?? result.tickets.length);
        } catch {
            setListError('Unable to load tickets. Please try again.');
        } finally {
            setListLoading(false);
        }
    }, [token, page, rowsPerPage, statusFilter, search]);

    useEffect(() => {
        loadList();
    }, [loadList]);

    const loadDetail = useCallback(async (ticketId: number) => {
        setDetailLoading(true);
        setDetailError('');
        try {
            const response = await SupportTicketServices.getTicket(token, ticketId);
            if (!response.ok) {
                setDetailError(await errorMessage(response));
                return;
            }
            setDetail(await response.json());
        } catch {
            setDetailError('Unable to load the ticket. Please try again.');
        } finally {
            setDetailLoading(false);
        }
    }, [token]);

    const openTicket = (ticket: ISupportTicket) => {
        setSelectedId(ticket.id);
        setDetail(null);
        setReply('');
        setReplyError('');
        loadDetail(ticket.id);
    };

    const closeTicket = () => {
        setSelectedId(null);
        setDetail(null);
    };

    const sendReply = async () => {
        if (sendLock.current || selectedId === null || !reply.trim()) return;

        // Taken before the confirmation dialog so repeated clicks can't queue extra sends.
        sendLock.current = true;
        try {
            const confirmed = await confirm({
                title: 'Send reply?',
                text: 'This reply will be emailed to the customer by Zendesk.',
                confirmLabel: 'Send reply'
            });
            if (!confirmed) return;

            setSending(true);
            setReplyError('');
            const response = await SupportTicketServices.reply(token, selectedId, reply.trim());
            if (!response.ok) {
                setReplyError(await errorMessage(response));
                return;
            }
            setReply('');
            setToast('Reply sent to the customer.');
            await Promise.all([loadDetail(selectedId), loadList()]);
        } catch {
            setReplyError('Unable to send the reply. Please try again.');
        } finally {
            sendLock.current = false;
            setSending(false);
        }
    };

    const changeStatus = async (status: TicketStatus) => {
        if (selectedId === null || !detail || status === detail.status) return;

        if (status === 'solved') {
            const confirmed = await confirm({
                title: 'Mark as solved?',
                text: 'The customer will be notified that this ticket is solved.',
                confirmLabel: 'Mark solved'
            });
            if (!confirmed) return;
        }

        setStatusUpdating(true);
        setReplyError('');
        try {
            const response = await SupportTicketServices.updateStatus(token, selectedId, status);
            if (!response.ok) {
                setReplyError(await errorMessage(response));
                return;
            }
            setToast(`Ticket status updated to ${status}.`);
            await Promise.all([loadDetail(selectedId), loadList()]);
        } catch {
            setReplyError('Unable to update the status. Please try again.');
        } finally {
            setStatusUpdating(false);
        }
    };

    const columns: TableColumn<ISupportTicket>[] = [
        { name: 'Ticket', selector: (row) => `#${row.id}`, maxWidth: '90px' },
        { name: 'Subject', selector: (row) => row.subject, minWidth: '220px', wrap: true },
        { name: 'Customer', selector: (row) => row.requester?.name || '-', maxWidth: '180px' },
        { name: 'Email', selector: (row) => row.requester?.email || '-', maxWidth: '220px' },
        {
            name: 'Status',
            cell: (row) => <Chip size="small" label={row.status} color={statusColor(row.status)} sx={{ textTransform: 'capitalize' }} />,
            maxWidth: '110px'
        },
        { name: 'Created', selector: (row) => formatDate(row.createdAt), minWidth: '170px' },
        { name: 'Updated', selector: (row) => formatDate(row.updatedAt), minWidth: '170px' }
    ];

    const isFinished = detail?.status === 'closed';

    return (
        <div className='body-sub-contianer table-container'>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel id="ticket-status-filter">Status</InputLabel>
                    <Select
                        labelId="ticket-status-filter"
                        label="Status"
                        value={statusFilter}
                        onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                    >
                        <MenuItem value="">All</MenuItem>
                        {STATUS_FILTERS.map((status) => (
                            <MenuItem key={status} value={status} sx={{ textTransform: 'capitalize' }}>{status}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Button startIcon={<RefreshIcon />} onClick={loadList} disabled={listLoading}>Refresh</Button>
                {listLoading && <CircularProgress size={20} />}
            </Box>

            {listError && (
                <Alert severity="error" sx={{ mb: 1 }} action={<Button color="inherit" size="small" onClick={loadList}>Retry</Button>}>
                    {listError}
                </Alert>
            )}

            {!listError && !listLoading && tickets.length === 0 && (
                <Alert severity="info" sx={{ mb: 1 }}>No tickets found.</Alert>
            )}

            <DataGrid
                title='Support Tickets'
                search='Search subject or customer'
                columns={columns}
                data={tickets}
                onSearch={(text: string) => { setSearch(text); setPage(1); }}
                onChangeRowsPerPage={(perPage: number, currentPage: number) => { setRowsPerPage(perPage); setPage(currentPage); }}
                onChangePage={(nextPage: number) => setPage(nextPage)}
                onRowClicked={(row: ISupportTicket) => openTicket(row)}
                pagination
                paginationTotalRows={totalRows}
            />

            <Dialog open={selectedId !== null} onClose={closeTicket} fullWidth maxWidth="md">
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span style={{ flex: 1 }}>{detail ? `#${detail.id} · ${detail.subject}` : `Ticket #${selectedId}`}</span>
                    <IconButton aria-label="Close" onClick={closeTicket}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {detailLoading && !detail && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}
                    {detailError && (
                        <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => selectedId !== null && loadDetail(selectedId)}>Retry</Button>}>
                            {detailError}
                        </Alert>
                    )}

                    {detail && (
                        <>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                                <Typography variant="body2">
                                    {detail.requester?.name} {detail.requester?.email && `(${detail.requester.email})`}
                                </Typography>
                                <Chip size="small" label={detail.status} color={statusColor(detail.status)} sx={{ textTransform: 'capitalize' }} />
                                <FormControl size="small" sx={{ minWidth: 150 }} disabled={statusUpdating || isFinished}>
                                    <InputLabel id="ticket-status-update">Set status</InputLabel>
                                    <Select
                                        labelId="ticket-status-update"
                                        label="Set status"
                                        value={SETTABLE_STATUSES.includes(detail.status) ? detail.status : ''}
                                        onChange={(e) => changeStatus(e.target.value as TicketStatus)}
                                    >
                                        {SETTABLE_STATUSES.map((status) => (
                                            <MenuItem key={status} value={status} sx={{ textTransform: 'capitalize' }}>{status}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                {statusUpdating && <CircularProgress size={18} />}
                            </Box>

                            <Box sx={{ display: 'grid', gap: 1.5, mb: 3 }}>
                                {detail.conversation.map((message) => (
                                    <Paper
                                        key={message.id}
                                        variant="outlined"
                                        sx={{
                                            p: 1.5,
                                            bgcolor: !message.isPublic ? 'warning.light' : message.author === 'support' ? 'action.hover' : 'background.paper'
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                            <Typography variant="subtitle2">{message.authorName}</Typography>
                                            {!message.isPublic && <Chip size="small" label="Internal note" />}
                                            <Typography variant="caption" color="text.secondary">{formatDate(message.createdAt)}</Typography>
                                        </Box>
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{message.body}</Typography>
                                    </Paper>
                                ))}
                            </Box>

                            {replyError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setReplyError('')}>{replyError}</Alert>}

                            <TextField
                                label="Reply to customer"
                                value={reply}
                                onChange={(e) => setReply(e.target.value)}
                                multiline
                                minRows={3}
                                fullWidth
                                disabled={sending || isFinished}
                                helperText={isFinished ? 'Closed tickets cannot be replied to.' : `${reply.length}/5000`}
                                slotProps={{ htmlInput: { maxLength: 5000 } }}
                            />
                            <Box sx={{ mt: 1.5, display: 'flex', gap: 1, alignItems: 'center' }}>
                                <Button
                                    variant="contained"
                                    onClick={sendReply}
                                    disabled={sending || isFinished || !reply.trim()}
                                >
                                    {sending ? 'Sending…' : 'Send reply'}
                                </Button>
                                {sending && <CircularProgress size={20} />}
                            </Box>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            <Dialog open={confirmState !== null} onClose={() => closeConfirm(false)} aria-labelledby="support-confirm-title" maxWidth="xs" fullWidth>
                <DialogTitle id="support-confirm-title">{confirmState?.title}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{confirmState?.text}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => closeConfirm(false)}>Cancel</Button>
                    <Button variant="contained" onClick={() => closeConfirm(true)} autoFocus>{confirmState?.confirmLabel}</Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast('')} message={toast} />
        </div>
    );
};

export default SupportTickets;
